import { ICellRendererParams } from 'ag-grid-community'
import { ProjectPlanCumulation } from '../../../../../lib/functions/projectPlan'
import { RootDiv } from '../../../commons/AgGrid/components/cell/CellRendererRoot'
import {
  FractionLabel,
  IconArea,
  PercentageLabel,
  StatusNameLabel,
} from '../../../commons/AgGrid/components/cell/custom/wbsItemStatus/cellRenderer'
import { FloatingEditableIcon } from '../../../commons/AgGrid/components/cell/common/text'
import { NewWbsItemRow } from '../../../../pages/ProjectPlanNew/projectPlanNew'
import {
  getWbsItemStatusDeepColorCode,
  getWbsItemStatusLabel,
} from '../../../../../lib/functions/wbsItem'
import { ProgressIcon } from '../../../commons/AgGrid/components/cell/custom/wbsItemStatus/ProgressIcon'
import { DelayIcon } from '../../../commons/AgGrid/components/cell/custom/wbsItemStatus/delayIcon'
import { isProduction } from '../../../../../utils/urls'
import { useMemo } from 'react'
import { FontSize, FontWeight } from '../../../../../styles/commonStyles'
import { colorPalette } from '../../../../style/colorPalette'

const Fraction = ({ total, done }: { total: number; done: number }) => {
  const displayVal = useMemo(() => {
    return `(${done} / ${total})`
  }, [total, done])
  return (
    <div
      style={{
        fontSize: FontSize.SMALL,
        color: colorPalette.monotone[4],
        fontWeight: FontWeight.NORMAL,
      }}
    >
      {displayVal}
    </div>
  )
}

export interface Params extends ICellRendererParams {
  hideChildrenCount?: boolean
}

export const ProjectPlanStatusCellRenderer = (params: Params) => {
  const wbsItem: NewWbsItemRow =
    params.data?.body?.wbsItem ?? params.data?.wbsItem
  const cumulation: ProjectPlanCumulation =
    params.data?.body?.cumulation ?? params.data?.cumulation
  if (!wbsItem || !params.colDef) {
    return <></>
  }

  if (!cumulation) {
    return (
      <RootDiv>
        <StatusNameLabel>
          {getWbsItemStatusLabel(wbsItem.status!)}
        </StatusNameLabel>
      </RootDiv>
    )
  }
  if (wbsItem.wbsItemType.isTask()) {
    const total = cumulation.countTask - cumulation.countStatusTaskDiscard
    const done = cumulation.countStatusTaskDone
    return (
      <RootDiv>
        <IconArea>
          {isProduction ? (
            <DelayIcon
              wbsItem={{
                ...wbsItem,
                createdAt: undefined,
                updatedAt: undefined,
              }}
            />
          ) : (
            <ProgressIcon
              wbsItem={{
                ...wbsItem,
                createdAt: undefined,
                updatedAt: undefined,
              }}
            />
          )}
        </IconArea>
        {wbsItem.wbsItemType.isTask() && (
          <StatusNameLabel>
            {getWbsItemStatusLabel(wbsItem.status!)}
          </StatusNameLabel>
        )}
        <div>
          {0 < total && !params.hideChildrenCount ? (
            isProduction ? (
              <FractionLabel>
                ({done} / {total})
              </FractionLabel>
            ) : (
              <div style={{ marginLeft: '6px' }}>
                <Fraction total={total} done={done} />
              </div>
            )
          ) : undefined}
        </div>
        <FloatingEditableIcon {...params} />
      </RootDiv>
    )
  }

  const total = wbsItem.wbsItemType.isDeliverable()
    ? cumulation.countTask - cumulation.countStatusTaskDiscard
    : cumulation.countDeliverable - cumulation.countStatusDeliverableDiscard
  const done = wbsItem.wbsItemType.isDeliverable()
    ? cumulation.countStatusTaskDone
    : cumulation.countStatusDeliverableDone
  return (
    <RootDiv>
      <IconArea>
        {isProduction ? (
          <DelayIcon
            wbsItem={{ ...wbsItem, createdAt: undefined, updatedAt: undefined }}
          />
        ) : (
          <ProgressIcon
            wbsItem={{ ...wbsItem, createdAt: undefined, updatedAt: undefined }}
          />
        )}
      </IconArea>
      <PercentageLabel total={total}>
        {!total ? '0.0%' : `${((done / total) * 100).toFixed(1)}%`}
      </PercentageLabel>
      <div>
        {isProduction ? (
          <FractionLabel>{`(${done} / ${total})`}</FractionLabel>
        ) : (
          <div style={{ marginLeft: '6px' }}>
            <Fraction total={total} done={done} />
          </div>
        )}
      </div>
      <FloatingEditableIcon {...params} />
    </RootDiv>
  )
}
